import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

interface QCFailDeleteWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  /** Opens the QC History modal for this track — where the operator can
   *  click the "Update" button next to the failed QC record and upload
   *  a corrected file. */
  onOpenQC: () => void;
  /** Falls through to the destructive delete. Single-click destructive —
   *  no secondary confirmation (this modal is already the warning). */
  onDeleteAnyway: () => void;
}

/**
 * Warning modal shown when the operator tries to delete a track whose
 * most recent QC result is 'fail'. Steers users toward the correct
 * workflow (open QC modal → click Update → upload corrected file)
 * which preserves the QC audit trail AND every campaign link that
 * references this track's audiometa doc id.
 *
 * The plain "Delete Anyway" button is still there for cases where the
 * operator genuinely wants the track gone (e.g. it was a mistaken
 * upload, campaign was scrapped) — just make it a conscious choice
 * rather than the default action.
 */
const QCFailDeleteWarningModal: React.FC<QCFailDeleteWarningModalProps> = ({
  isOpen,
  onClose,
  trackName,
  onOpenQC,
  onDeleteAnyway,
}) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    contentClassName="delete-confirm-modal"
    style={{ color: '#fff', maxWidth: '560px' }}
  >
    <ModalHeader
      toggle={onClose}
      style={{
        backgroundColor: 'var(--chatbot-color-2)',
        color: '#fff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <span style={{ color: '#fff' }}>
        This track failed QC — fix it instead of deleting?
      </span>
    </ModalHeader>

    <ModalBody style={{
      backgroundColor: 'var(--chatbot-color-1)',
      color: '#fff',
      padding: '1.5rem',
    }}>
      <div style={{
        padding: '0.85rem 1rem',
        backgroundColor: 'rgba(255, 80, 80, 0.08)',
        border: '1px solid rgba(255, 80, 80, 0.25)',
        borderRadius: '6px',
        marginBottom: '1rem',
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: '0.9rem',
        lineHeight: 1.5,
      }}>
        Deleting <strong style={{ color: '#fff' }}>{trackName}</strong> removes
        it from the library entirely. Every campaign that references it will
        lose its link and need to be re-matched.
      </div>

      <div style={{
        marginBottom: '1rem',
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: '0.9rem',
        lineHeight: 1.55,
      }}>
        <div style={{ marginBottom: '0.6rem', color: '#fff', fontWeight: 600 }}>
          Recommended: fix the track in place
        </div>
        <ol style={{ paddingLeft: '1.25rem', margin: 0 }}>
          <li style={{ marginBottom: '0.35rem' }}>Open this track's QC History modal</li>
          <li style={{ marginBottom: '0.35rem' }}>Click the <strong style={{ color: '#ffb432' }}>Update</strong> button next to the failed QC record — the track edit page opens</li>
          <li style={{ marginBottom: '0.35rem' }}>Upload the corrected file and save - Do not delete the failed file</li>
          <li>Open the QC modal again and submit the new version for QC</li>
        </ol>
        <div style={{ marginTop: '0.6rem', color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.8rem' }}>
          This keeps every campaign link intact AND preserves the QC audit trail.
        </div>
      </div>
    </ModalBody>

    <ModalFooter style={{
      backgroundColor: 'var(--chatbot-color-2)',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      gap: '0.5rem',
    }}>
      <Button
        className="btn-info-custom"
        onClick={onClose}
      >
        <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
      </Button>
      <Button
        className="btn-danger-custom"
        onClick={() => { onDeleteAnyway(); onClose(); }}
        title="Deletes the track — every campaign link to it will break"
      >
        <span style={{ position: 'relative', zIndex: 2 }}>Delete Anyway</span>
      </Button>
      <Button
        className="btn-success-custom"
        onClick={() => { onOpenQC(); onClose(); }}
      >
        <span style={{ position: 'relative', zIndex: 2 }}>Open QC Modal</span>
      </Button>
    </ModalFooter>
  </Modal>
);

export default QCFailDeleteWarningModal;
